/* eslint-disable local-rules/no-invalid-flag-reference */

/**
 * @mc/flags
 *
 * The feature flag system manages passing flag data between the server
 * and the client, and provides mechanisms for checking them in your JS
 * code — checking flags is always synchronous. All bucketing (determining if
 * a flag is on or off for a given user or UTMA) is done on the sever-side.
 *
 * To setup a feature flag, add the `js_readable = true` annotation
 * to your flag configuration in `flags.ini`:
 *
 * ```ini
 * [steven.rose_shield]
 * enabled = 0.1
 * js_readable = true
 * ```
 *
 * Then, you can check it in JavaScript like this:
 *
 * ```js
 * import { isOn } from '@mc/flags';
 *
 * if (isOn(FLAGS.STEVEN_ROSE_SHIELD)) {
 *  summonShield();
 * }
 * ```
 *
 * Or with the `Flag` React component:
 *
 * ```js
 * <Flag isOn={FLAGS.STEVEN_ROSE_SHIELD}>
 *   <RoseShield />
 * </Flag>
 * ```
 *
 * this component requires one of `isOn` or `isOff`, and
 * it will throw on mount if neither _or both_ exist. It will
 * also throw when `CallExpression`s are found in the subtree. This
 * ensures that code behind a flag is not inadvertantly evaluated
 * when the flag is off. For example:
 *
 * ```js
 * <Flag isOn={FLAGS.STEVEN_ROSE_SHIELD}>
 *   <RoseShield onClick={createShieldHandler(user.id)} />
 * </Flag>
 * ```
 *
 * Here, we are executing `createShieldHandler` every render,
 * even if the flag is not enabled. When we detect this happening
 * we throw a `SyntaxError` and encourage users to move the
 * function execution into their wrapped component so that we
 * only evaluate that code when the flag is enabled.
 *
 * In production, the flag names are replaced with md5 hashes:
 *
 * ```js
 * if (isOn(FLAGS["3cb195abb3663d77fd719160c1ce904b"])) { }
 * ```
 *
 * in dev these include the cleartext flag name after the hash:
 *
 * ```js
 * if (isOn(FLAGS["3cb195abb3663d77fd719160c1ce904b_STEVEN_ROSE_SHIELD"])) { }
 * ```
 *
 * These transformations are implemented in
 * `web-config/babel-plugins/transform-obfuscate-flagnames.js`
 *
 */
import PropTypes from 'prop-types';
import { usePageDataStore } from '@mc/globalStore';
const getFlags = () => {
  if (__TEST__) {
    return {};
  }
  const flags = usePageDataStore.getState().flags;
  return Object.freeze(flags);
};

// This private variable stores the enabled boolean states
// of all the readable flags. It is fetched from the GetPageData
// autolyse call, and its value is set via the `setFlags` function
// below imported in `useBootstrapMailchimp`. It is mutable to allow mocking in tests.
let flags = window.xp_reactify_gpd ? {} : getFlags();

export const setFlags = (data) => {
  if (Object.keys(flags).length === 0) {
    flags = data;
  }
};

/**
 * Mock out your flag state for tests. Each call to `mockFlags` will
 * replace _not extend_ the flag state. For example:
 *
 * ```js
 * import {isOn, mockFlags} from '@mc/flags';
 *
 * mockFlags({ STEVEN_ROSE_SHIELD: true });
 * expect(isOn(FLAGS.STEVEN_ROSE_SHIELD)).toBe(true);
 *
 * // replace mocked state
 * mockFlags({ RUBY_AND_SAFIRE_FUSED: false  });
 * expect(() => isOn(FLAGS.STEVEN_ROSE_SHIELD)).toThrowError();
 * expect(isOn(FLAGS.RUBY_AND_SAFIRE_FUSED)).toBe(false);
 * ```
 *
 * This will be applied globally, so flag checks inside of the modules
 * you are testing will use the mocked state.
 *
 * This behavior is only available in the test environment where `__TEST__ === true`
 *
 * @param {Object} mock the state mocked out by the flags
 */
const mockFlags = __TEST__
  ? (mock) => {
      // Clobber the module-local `flags` var.
      flags = mock;
      global.FLAGS = new Proxy(
        {},
        {
          get: (_, key) => {
            // return key if NOT all caps
            if (!/^[A-Z]+$/.test(key)) {
              return key;
            }
            if (!mock.hasOwnProperty(key)) {
              throw new Error(
                `${key} not a valid flag — flags in tests must be mocked. See mockFlags in the @mc/flags module for more info.`,
              );
            }
            return key;
          },
        },
      );
    }
  : () => {
      throw new Error('mockFlags can only be used in a testing environment');
    };

/**
 * Check if a given entry from `FLAGS` is enabled.
 *
 * ```js
 * isOn(FLAGS.MY_COOL_FLAG)
 * ```
 *
 * @param {String} flagName resolve a constant from `FLAGS` and return true if enabled
 * @returns {Boolean}
 */
const isOn = (flagName) => Boolean(flags[flagName]);

/**
 * Check if a given entry from `FLAGS` is disabled.
 *
 * ```js
 * isOff(FLAGS.MY_COOL_FLAG)
 * ```
 *
 * @param {String} flagName resolve a constant from `FLAGS` and return true if disabled
 * @returns {Boolean}
 */
const isOff = (flagName) => !isOn(flagName);

/**
 * Flag component for putting JSX behind feature flags:
 *
 * ```js
 * <Flag isOn={FLAGS.MY_COOL_FLAG}>{() => <MyFeature />}</Flag>
 * ```
 *
 * Requires one of `isOn` or `isOff` props, and will throw an exception in dev if it has
 * neither, or both. Also requires that children be a function and will, again, throw in
 * the case that it's not.
 *
 * @param {Object} props `isOn` or `isOff`
 */
const Flag = (props) => {
  if (__DEV__ || __TEST__) {
    if (props.isOn && props.isOff) {
      throw new Error(
        '<Flag> component: mutually exclusive props: [isOn, isOff]. Both were passed.',
      );
    }
    if (!props.isOn && !props.isOff) {
      throw new Error(
        '<Flag> component: requires one of these props [isOn, isOff]',
      );
    }
  }
  if (props.isOn) {
    return isOn(props.isOn) ? props.children : null;
  }
  if (props.isOff) {
    return isOff(props.isOff) ? props.children : null;
  }
  return null;
};

Flag.propTypes = {
  children: PropTypes.node.isRequired,
  isOff: PropTypes.string,
  isOn: PropTypes.string,
};

/**
 * Exposes all flag constants which are marked as `js_readable` in `flags.ini`.
 *
 * Check with `isOn, isOff, Flags` exports:
 *
 * ```js
 * isOn(FLAGS.MY_FLAG);
 * isOff(FLAGS.MY_FLAG);
 * <Flag isOn={FLAGS.MY_FLAG}>{() => <MyFeature />}</Flag>
 * ```
 *
 * In dev and test envs it will throw if a non-existant flag is checked.
 *
 * The keys will be replaced with hashes in prod:
 *
 * ```js
 * FLAGS.MY_FLAG —> FLAGS["6d4e2b434667cb311b09be2c3bb8a311"]
 * ```
 *
 * As such, only dot-notation literals allowed in source code — the babel
 * plugin will throw an exception if you attempt to access a flag as a computed
 * property:
 *
 * ```js
 * FLAGS["MY_FLAG"] // throws SyntaxError
 * ```
 *
 */

if (__DEV__) {
  window.dumpFlags = () => flags;
}

export { Flag, isOn, isOff, mockFlags };
